import React from "react";
import { ReactComponent as PersonIcon } from "../../../../images/person_blue.svg";
import MetricSummaryTile from "../../../../ui-components/metric-summary-tile/metricSummaryTile";
import { PickupStatsSummary } from "../../../../app/data/pickup-requests/models";
import { PickupReviewStatus } from "../../../../app/data/communications/models";
import XGSIcon from "../../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../../ui-components/icon/xgsIcons";
import { formatCount, formatPercent } from "../utils/pickupStatsFormatters";

export type InboxSummaryFilter =
  | { type: "hasPendingReview" }
  | { type: "reviewStatus"; value: PickupReviewStatus }
  | { type: "clearOutcomes" }
  | null;

export interface InboxSummaryTilesProps {
  stats?: PickupStatsSummary;
  isLoading: boolean;
  filtersApplied: boolean;
  onApplyFilter: (filter: InboxSummaryFilter) => void;
}

const periodSuffix = (stats?: PickupStatsSummary) =>
  stats?.period?.label ?? "Last 7 days";

const InboxSummaryTiles: React.FC<InboxSummaryTilesProps> = ({
  stats,
  isLoading,
  filtersApplied,
  onApplyFilter,
}) => {
  const backlog = stats?.backlog;
  const outcomes = stats?.outcomes;
  const derived = stats?.derived;
  const periodLabel = periodSuffix(stats);
  const pendingReviewTaskCount = backlog?.pendingReviewTasks ?? 0;
  const approvedByHuman = outcomes?.approvedByHuman ?? 0;
  const rejectedByHuman = outcomes?.rejectedByHuman ?? 0;

  return (
    <div className="xgs-pickup-inbox__summary">
      <div className="xgs-pickup-inbox__summary-row">
        <MetricSummaryTile
          iconPreset="filePurplePending"
          colorVariant="purple"
          isLoading={isLoading}
          title="Pending review"
          value={backlog ? formatCount(pendingReviewTaskCount) : 0}
          legendDescription="Review tasks awaiting human review."
          onApply={() => onApplyFilter({ type: "hasPendingReview" })}
        />
        <MetricSummaryTile
          iconPreset="fileBlue"
          colorVariant="blue"
          isLoading={isLoading}
          title={`Received · ${periodLabel}`}
          value={outcomes ? formatCount(outcomes.received) : 0}
          legendDescription="Inbound emails received in the selected period."
          onApply={() => onApplyFilter({ type: "clearOutcomes" })}
        />
        <MetricSummaryTile
          iconPreset="fileGreenCheck"
          colorVariant="green"
          isLoading={isLoading}
          title={`Auto-created · ${periodLabel}`}
          value={
            outcomes ? (
              <span>
                {formatCount(outcomes.autoCreated)}
                {derived && (
                  <span className="xgs-pickup-inbox__summary-subtitle">
                    {" "}
                    · {formatPercent(derived.automationRate)}
                  </span>
                )}
              </span>
            ) : (
              0
            )
          }
          legendDescription="Pickups auto-created by the system, with automation rate for the period."
          onApply={() => onApplyFilter({ type: "reviewStatus", value: "APPROVED_BY_SYSTEM" })}
        />
        <MetricSummaryTile
          icon={<PersonIcon />}
          colorVariant="blue"
          isLoading={isLoading}
          title={`Employee approved/rejected · ${periodLabel}`}
          value={
            outcomes ? (
              <span>
                {formatCount(approvedByHuman)}
                <span className="xgs-pickup-inbox__summary-subtitle"> / </span>
                {formatCount(rejectedByHuman)}
              </span>
            ) : (
              "0 / 0"
            )
          }
          legendDescription="Employee-approved and employee-rejected pickups in the selected period."
        />
      </div>

      {filtersApplied && (
        <div className="xgs-pickup-inbox__summary-filters-note">
          <XGSIcon
            icon={XGSIcons.faExclamationCircle}
            size="sm"
            className="xgs-pickup-inbox__summary-filters-note__icon"
          />
          Filters applied to results below.
        </div>
      )}
    </div>
  );
};

export default InboxSummaryTiles;
